import React from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/hooks/useStore';
import { useData } from '@microsoft/teamsfx-react';
import { Card, mergeClasses, Spinner } from '@fluentui/react-components';
import EnhancedExternalLink from 'src/components/atoms/enhanced-external-link/EnhancedExternalLink';
import WidgetContainer from 'src/components/molecules/widget-container/WidgetContainer';
import { ProgramType } from 'src/utils/enums';
import { ProgramParticipantStatus } from 'src/models/rewards/Rewards';
import ClassicActionProgramCard from '../../molecules/classic-action-program-card/ClassicActionProgramCard';
import CumulativePointsProgramCard from '../../organisms/cumulative-points-program-card/CumulativePointsProgramCard';
import { useRewardsSectionStyles } from './RewardsSection.styles';

const RewardsSection: React.FC = () => {
  const classes = useRewardsSectionStyles();
  const { rewardsStore } = useStore();
  const { loading: isRewardsLoading, error: loadRewardsError } = useData(rewardsStore.loadRewards);

  if (loadRewardsError) {
    throw loadRewardsError;
  }

  if (isRewardsLoading) {
    return <Spinner aria-label="Loading rewards..." />;
  }

  const rewards = rewardsStore?.rewards;
  if (!rewards?.programStatuses?.length) {
    return null;
  }

  const { rewardsTitle, programStatuses } = rewards;
  const renderProgramCard = (participantStatus: ProgramParticipantStatus): JSX.Element | null => {
    const { programId, programType } = participantStatus;

    switch (programType) {
      case ProgramType.CumulativePoints:
        return <CumulativePointsProgramCard key={programId} participantStatus={participantStatus} />;
      case ProgramType.ClassicAction:
        return <ClassicActionProgramCard key={programId} participantStatus={participantStatus} />;
      default:
        return (
          <Card key={programId}>
            <div data-testid="invalid-program-type-message" className={classes.invalidProgramTypeMessage}>
              <div>An unexpected program type was encountered.</div>
              <div>Please contact support for assistance.</div>
            </div>
          </Card>
        );
    }
  };

  return (
    <WidgetContainer
      className={mergeClasses('rewards-section', classes.rewardsSection)}
      title={rewardsTitle}
      footer={<EnhancedExternalLink as="a">View rewards</EnhancedExternalLink>}
    >
      <div className={classes.cardsContainer}>
        {programStatuses?.map(renderProgramCard)}
      </div>
    </WidgetContainer>
  );
};

export default observer(RewardsSection);
